// The Zooylo owner console — phase 1 of the Sales portal
// (docs/revamp/SALES_PORTAL_REQUIREMENTS.md). Platform admins only.
//
// GATE. Signed out → the admin sign-in. Signed in → am_i_platform_admin(); a
// "no" renders an ordinary not-found screen, so the console's existence is not
// advertised to anyone else. Every RPC behind it also refuses a non-platform
// admin (42501), so the gate is courtesy, not the control.
//
// Demo mode (no Supabase) shows the console over the fixtures so it can be
// reviewed; nothing in it can reach a real organisation.

import { useState } from "react";
import { useComfort } from "../ComfortContext";
import { getDictionary, LOCALE_LABEL, type Locale } from "../../i18n/dictionary";
import { getPortalData, shouldUseSupabase } from "../../data";
import { useSession } from "../../lib/auth";
import { AdminSignIn } from "../auth/AdminSignIn";
import { LinkSent } from "../auth/LinkSent";
import { sendMagicLink, adminRedirectUrl } from "../auth/sendMagicLink";
import { getSupabaseClient } from "../../lib/supabase";
import { Button, Notice, Tabs, useLoad } from "../admin/ui";
import { ConsoleOrgs } from "./ConsoleOrgs";
import { ConsoleCodes } from "./ConsoleCodes";
import "../../components/portal.css";

export function ConsoleGate() {
  return shouldUseSupabase() ? <ConsoleGateLive /> : <ConsoleShell demo />;
}

function ConsoleGateLive() {
  const { locale, setLocale } = useComfort();
  const session = useSession();
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (session.status === "loading") return <Splash />;
  if (session.status === "signed_out") {
    if (sentTo) {
      return (
        <LinkSent
          locale={locale}
          email={sentTo}
          onResend={() => void sendMagicLink(sentTo, adminRedirectUrl())}
          onDifferentEmail={() => setSentTo(null)}
        />
      );
    }
    return <AdminSignIn locale={locale} setLocale={setLocale} onSent={setSentTo} />;
  }
  return <ConsoleAuthorised email={session.email} />;
}

function ConsoleAuthorised({ email }: { email?: string }) {
  const { data, loading } = useLoad(() => getPortalData().amIPlatformAdmin(), []);
  if (loading) return <Splash />;
  if (data !== true) return <NotFound />;
  return (
    <ConsoleShell
      email={email}
      onSignOut={() => {
        void getSupabaseClient().auth.signOut().catch(() => {});
      }}
    />
  );
}

function Splash() {
  return (
    <div className="zp-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <span aria-label="Loading" style={{ width: 28, height: 28, border: "3px solid var(--line-strong)", borderTopColor: "var(--brand)", borderRadius: "50%", animation: "zspin .8s linear infinite" }} />
    </div>
  );
}

/** Deliberately indistinguishable from any other unknown address. */
export function NotFound() {
  const { locale } = useComfort();
  const t = getDictionary(locale).console;
  return (
    <div className="zp-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, background: "var(--bg)", fontFamily: "var(--font-body)", textAlign: "center" }}>
      <div style={{ maxWidth: 420 }}>
        <h1 style={{ fontSize: 26, color: "var(--ink-deep)", margin: 0 }}>{t.notFoundTitle}</h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 16, marginTop: 10 }}>{t.notFoundBody}</p>
        <a href="/" style={{ display: "inline-block", marginTop: 14, color: "var(--brand)", fontWeight: 600 }}>
          {t.backHome}
        </a>
      </div>
    </div>
  );
}

type Tab = "orgs" | "codes";

export function ConsoleShell({ demo = false, email, onSignOut }: { demo?: boolean; email?: string; onSignOut?: () => void }) {
  const { locale, setLocale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.console;
  const [tab, setTab] = useState<Tab>("orgs");
  const next: Locale = locale === "en" ? "ms" : "en";

  return (
    <div className="zp-root" style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh" }}>
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 22, color: "var(--brand)" }}>zooylo</span>
            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-faint)" }}>{t.eyebrow}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {email && <span style={{ fontSize: 13.5, color: "var(--ink-soft)", overflowWrap: "anywhere" }}>{email}</span>}
            <Button variant="secondary" onClick={() => setLocale(next)}>
              {LOCALE_LABEL[next]}
            </Button>
            {onSignOut && (
              <Button variant="secondary" onClick={onSignOut}>
                {dict.common.signOut}
              </Button>
            )}
          </div>
        </div>
      </header>
      <main style={{ maxWidth: 1160, margin: "0 auto", padding: "24px 16px 60px" }}>
        <h1 style={{ fontSize: 28, color: "var(--ink-deep)", margin: 0 }}>{t.title}</h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 16, margin: "6px 0 16px" }}>{t.subtitle}</p>
        {demo && (
          <div style={{ marginBottom: 16 }}>
            <Notice tone="info">{t.demoNote}</Notice>
          </div>
        )}
        <Tabs
          label={t.title}
          value={tab}
          onChange={setTab}
          tabs={[
            { key: "orgs", label: t.tabOrgs },
            { key: "codes", label: t.tabCodes },
          ]}
        />
        <div style={{ marginTop: 18 }}>{tab === "orgs" ? <ConsoleOrgs /> : <ConsoleCodes />}</div>
      </main>
    </div>
  );
}

